/** Etiquetas para métodos de pago (alineado con backend PaymentMethod). */
export const ABONO_PAYMENT_LABELS = {
  STRIPE: 'Tarjeta (Stripe / online)',
  TPV: 'TPV / datáfono',
  CASH: 'Efectivo',
  BIZUM: 'Bizum',
  BANK_TRANSFER: 'Transferencia',
  AGENCY: 'Colaborador / agencia',
  NONE: 'Solo registro contable (abono manual fuera del sistema)',
};

const ABONO_SELECTABLE = ['STRIPE', 'TPV', 'CASH', 'BIZUM', 'BANK_TRANSFER', 'NONE'];

function normalizeMethod(p) {
  const m = p.paymentMethod ?? p.method;
  if (!m) return 'NONE';
  const u = String(m).toUpperCase();
  if (u === 'CARD' || u === 'TARJETA') return 'STRIPE';
  if (u === 'COLABORADOR' || u === 'AGENCY') return 'AGENCY';
  return u;
}

/**
 * Agrupa cobros previos (no REFUND) por método y recomienda el de mayor importe (criterio fiscal).
 * @param {Array} payments - pagos de la reserva
 * @returns {{ aggregated: Array<{method: string, totalEur: number, label: string}>, recommended: object|null }}
 */
export function getCreditAbonoPaymentHints(payments) {
  const rows = (payments || []).filter((p) => {
    if (p.status !== 'PAID') return false;
    const mt = p.movementType || 'PAYMENT';
    if (mt === 'REFUND') return false;
    return true;
  });

  const byMethod = new Map();
  for (const p of rows) {
    const method = normalizeMethod(p);
    const g = Number(p.grossAmount) || 0;
    if (g <= 0) continue;
    byMethod.set(method, (byMethod.get(method) || 0) + g);
  }

  const aggregated = [...byMethod.entries()]
    .map(([method, totalEur]) => ({
      method,
      totalEur: Math.round(totalEur * 100) / 100,
      label: ABONO_PAYMENT_LABELS[method] || method,
    }))
    .sort((a, b) => b.totalEur - a.totalEur);

  const recommended = aggregated.length > 0 ? aggregated[0] : null;

  return { aggregated, recommended, labels: ABONO_PAYMENT_LABELS, selectableMethods: ABONO_SELECTABLE };
}

/**
 * Opciones para el select: primero métodos con cobro previo (mayor a menor), luego el resto sin duplicar.
 */
export function buildCreditAbonoMethodOptions(hints) {
  const { aggregated, selectableMethods } = hints || { aggregated: [], selectableMethods: ABONO_SELECTABLE };
  const seen = new Set();
  const ordered = [];

  let rank = 0;
  for (const a of aggregated) {
    if (!selectableMethods.includes(a.method) && a.method !== 'AGENCY') continue;
    if (a.method === 'AGENCY') continue;
    if (seen.has(a.method)) continue;
    seen.add(a.method);
    rank += 1;
    const rec =
      rank === 1
        ? ' (recomendado: mayor cobro previo, coherencia fiscal)'
        : '';
    ordered.push({
      value: a.method,
      label: `${a.label} — cobrado ${a.totalEur.toFixed(2)} €${rec}`,
      isRecommended: rank === 1,
    });
  }

  for (const m of selectableMethods) {
    if (seen.has(m)) continue;
    seen.add(m);
    ordered.push({
      value: m,
      label: ABONO_PAYMENT_LABELS[m] || m,
      isRecommended: false,
    });
  }

  return ordered;
}
